import { useState } from 'react';
import { Download, Check, ShieldAlert, HardDrive, Info } from 'lucide-react';
import Button from '../ui/Button';
import type { NewPairingCode } from '../../services/espnowIrrigation';

/**
 * The files that turn a blank Raspberry Pi into a paired site, with nobody
 * technical at the far end.
 *
 * WHY THIS EXISTS
 *   A bridge on a Pi provisions itself from files left on the card's boot
 *   partition — it is FAT32, so it mounts as an ordinary drive on the Windows
 *   machine that flashes the image. Until now those files were hand-typed,
 *   which fails in two ways that both cost a site visit:
 *
 *     * Windows editors save "UTF-8 with BOM". A byte order mark is not
 *       whitespace, so it survives a trim, the code is rejected as malformed —
 *       and because pairing codes are single use, it is burnt. The bridge now
 *       strips a BOM defensively, but not creating one is better.
 *     * Explorer hides known extensions by default, so `factree-pairing-code.txt`
 *       typed into a "New Text Document" becomes factree-pairing-code.txt.txt
 *       and is never read.
 *
 *   A generated file has neither problem.
 *
 * WHY NOT A ZIP
 *   Because Windows' "Extract All" puts the contents in a SUBFOLDER named after
 *   the archive, and these files only work in the root of the partition. One
 *   download per file is a click more and a failure mode less.
 *
 * THE TAILSCALE KEY NEVER LEAVES THE BROWSER
 *   It is pasted here and written straight into a Blob. It is not sent to the
 *   API, not stored, and not logged. That matters because it is a far more
 *   sensitive credential than the pairing code beside it: a leaked pairing code
 *   compromises one site, a leaked tailnet auth key is network access to every
 *   device on the tailnet.
 */

/** LF, and explicitly no BOM. See the note above — this is the whole point. */
function downloadText(filename: string, body: string) {
  const blob = new Blob([body.replace(/\r\n/g, '\n')], {
    type: 'text/plain;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function pairingFile(code: string, siteLabel: string): string {
  return [
    `# Factree irrigation bridge - pairing code`,
    `# Site: ${siteLabel}`,
    `# Lines starting with # are ignored.`,
    ``,
    code,
    ``,
  ].join('\n');
}

function tailscaleFile(key: string, siteLabel: string): string {
  return [
    `# Factree irrigation bridge - Tailscale auth key`,
    `# Site: ${siteLabel}`,
    `# Consumed and deleted on first boot.`,
    ``,
    key,
    ``,
  ].join('\n');
}

function instructionsFile(siteLabel: string, withTailscale: boolean): string {
  return [
    `FACTREE IRRIGATION BRIDGE - INSTALLING THIS UNIT`,
    `================================================`,
    ``,
    `Site: ${siteLabel}`,
    ``,
    `WHAT YOU HAVE`,
    `  A Raspberry Pi, already set up. It needs no keyboard, no screen and`,
    `  nobody technical. Plug it in and it configures itself.`,
    ``,
    `BEFORE YOU GO TO SITE`,
    `  1. Put the memory card in this computer. It appears as a drive called`,
    `     "bootfs".`,
    `  2. Copy these files onto it:`,
    `        factree-pairing-code.txt`,
    ...(withTailscale ? [`        factree-tailscale-key.txt`] : []),
    ``,
    `     Put them in the TOP level of that drive, not inside a folder.`,
    `  3. Eject the card and put it back in the Pi.`,
    ``,
    `ON SITE`,
    `  1. Connect the aerial to the XBee radio.`,
    `  2. Plug the XBee radio into any USB socket on the Pi.`,
    `  3. Connect the network - either the Ethernet cable or the 4G stick.`,
    `  4. Plug in the power.`,
    ``,
    `  That is everything. Leave it powered on. It sorts itself out in about`,
    `  two minutes and appears in FactreeTech on its own.`,
    ``,
    `IF IT DOES NOT COME ONLINE`,
    `  You do not need a computer person for this.`,
    ``,
    `  1. Unplug the power.`,
    `  2. Take the memory card out and put it in any laptop.`,
    `  3. Open the file called  factree-status.txt`,
    ``,
    `  It says in plain English what happened and what to do about it. The`,
    `  most common answer is that the XBee radio was not plugged in when it`,
    `  started - in which case plug it in and power the Pi back on.`,
    ``,
    `NOTES`,
    `  The two files above delete themselves once used. That is normal and`,
    `  means it worked - do not put them back.`,
    ``,
  ].join('\n');
}

interface Props {
  issued: NewPairingCode;
  siteLabel: string;
  expiresLabel: string;
}

export default function XBeeProvisioningKit({ issued, siteLabel, expiresLabel }: Props) {
  const [tsKey, setTsKey] = useState('');
  const [got, setGot] = useState<Record<string, boolean>>({});

  const withTailscale = tsKey.trim().length > 0;

  function take(name: string, body: string) {
    downloadText(name, body);
    setGot(prev => ({ ...prev, [name]: true }));
  }

  const keyLooksWrong = withTailscale && !/^tskey-/i.test(tsKey.trim());

  return (
    <div className="p-4 rounded-lg border-2 border-blue-500 bg-blue-50 space-y-4">
      <div className="flex items-start gap-2">
        <HardDrive className="h-5 w-5 mt-0.5 shrink-0 text-blue-700" />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-blue-900">
            Provisioning kit for {siteLabel}
          </div>
          <div className="text-xs text-blue-800 mt-0.5">
            Code <span className="font-mono font-semibold tracking-wider">{issued.display_code}</span>
            {' · '}valid until {expiresLabel}
          </div>
        </div>
      </div>

      {/* Optional, and deliberately client-side only. */}
      <div>
        <label
          className="block text-sm font-medium mb-1 text-blue-900"
          htmlFor="tailscale-key"
        >
          Tailscale auth key{' '}
          <span className="font-normal text-blue-700">
            (optional — for remote access)
          </span>
        </label>
        <input
          id="tailscale-key"
          className="w-full rounded-md border border-blue-300 bg-white text-neutral-900 px-3 py-2 text-sm font-mono"
          placeholder="tskey-auth-…"
          value={tsKey}
          autoComplete="off"
          spellCheck={false}
          onChange={e => setTsKey(e.target.value)}
        />
        <div className="mt-1.5 flex items-start gap-1.5 text-xs text-blue-800">
          <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            Pasted here it goes straight into the downloaded file — it is never
            sent to the server or stored. Generate a <strong>single-use</strong>,
            pre-approved, tagged key, and turn off key expiry for the device
            afterwards or it drops off the tailnet in ~180 days.
          </span>
        </div>
        {keyLooksWrong && (
          <div className="mt-1 text-xs text-amber-700">
            That does not look like a Tailscale auth key — they start with{' '}
            <code>tskey-</code>.
          </div>
        )}
      </div>

      <div className="space-y-2">
        <div className="text-sm font-medium text-blue-900">
          Download onto the card
        </div>

        <KitFile
          name="factree-pairing-code.txt"
          done={got['factree-pairing-code.txt']}
          onClick={() => take('factree-pairing-code.txt', pairingFile(issued.code, siteLabel))}
        />

        {withTailscale && (
          <KitFile
            name="factree-tailscale-key.txt"
            done={got['factree-tailscale-key.txt']}
            onClick={() => take('factree-tailscale-key.txt', tailscaleFile(tsKey.trim(), siteLabel))}
          />
        )}

        <KitFile
          name="READ-ME-FIRST.txt"
          hint="Instructions for whoever installs it"
          done={got['READ-ME-FIRST.txt']}
          onClick={() => take('READ-ME-FIRST.txt', instructionsFile(siteLabel, withTailscale))}
        />
      </div>

      <div className="flex items-start gap-1.5 text-xs text-blue-800 pt-1 border-t border-blue-200">
        <Info className="h-3.5 w-3.5 mt-1.5 shrink-0" />
        <span className="pt-1">
          Copy these into the <strong>top level</strong> of the card's{' '}
          <code className="px-1 rounded bg-blue-100">bootfs</code>{' '}
          drive — not into a folder. The Pi reads them on first boot, uses them,
          and deletes them.
        </span>
      </div>
    </div>
  );
}

function KitFile(
  { name, hint, done, onClick }:
  { name: string; hint?: string; done?: boolean; onClick: () => void },
) {
  return (
    <div className="flex items-center justify-between gap-3 p-2 rounded-md bg-white/70">
      <div className="min-w-0">
        <div className="font-mono text-xs truncate">{name}</div>
        {hint && <div className="text-xs text-neutral-500 mt-0.5">{hint}</div>}
      </div>
      <Button variant={done ? 'ghost' : 'outline'} size="sm" onClick={onClick}>
        {done ? <Check className="h-4 w-4 mr-1" /> : <Download className="h-4 w-4 mr-1" />}
        {done ? 'Again' : 'Download'}
      </Button>
    </div>
  );
}
